(() => {
  if (customElements.get("product-media")) {
    return;
  }

  class ProductMedia extends HTMLElement {
    constructor() {
      super();

      this.mainSliderElement = this.querySelector(
        "[data-main-product-slider]"
      );

      this.mainSliderDrag =
        Number(this.mainSliderElement.dataset.slidesCount) !== 1;

      this.autoHeight = this.hasAttribute("data-autoheight");

      /*
       * Keep the ORIGINAL gallery slides.
       *
       * Important:
       * We never permanently remove variant images from
       * this array. Every variant change is rebuilt from
       * this original list.
       */
      this.originalMainSlides = [];
      this.originalThumbSlides = [];

      this.mainSlider = {
        element: this.mainSliderElement,
        bar: this.querySelector(
          "[data-main-product-slider-progress-bar]"
        ),
        instance: null,
        options: {
          pagination: false,
          type: "loop",
          waitForTransition: true,
          flickMaxPages: 0.5,
          arrows: false,
          drag: this.mainSliderDrag,
          speed: 300,
          easing: "ease-in-out",
          perPage: 1,
          perMove: 1,
          padding: { right: "10%" },
          gap: "1.6rem",
          clones: 1,
        },
      };

      this.thumbsSlider = {
        element: this.querySelector("[data-main-product-thumbs]"),
        nextArrow: this.querySelector("[data-thumbs-next]"),
        instance: null,
        options: {
          pagination: false,
          rewind: true,
          waitForTransition: true,
          flickMaxPages: 0.5,
          arrows: false,
          isNavigation: true,
          direction: "ltr",
          perPage: 3,
          gap: "1.6rem",
          speed: 300,
          easing: "ease-in-out",
          breakpoints: {
            749: {
              perPage: 4,
            },
          },
        },
      };

      this.controls = this.querySelectorAll("[data-control]");

      this.controls.forEach((control) => {
        control.addEventListener(
          "click",
          this.onControlClick.bind(this)
        );
      });

      if (this.thumbsSlider.nextArrow) {
        this.thumbsSlider.nextArrow.addEventListener("click", (event) => {
          this.onControlClick(
            event,
            this.thumbsSlider.instance
          );
        });
      }

      window.addEventListener(
        "DOMContentLoaded",
        this.initSlider.bind(this)
      );

      window.addEventListener(
        "resize",
        debounce(this.initSlider.bind(this), 500)
      );
    }

    connectedCallback() {
      /*
       * Capture the original slides BEFORE any variant
       * filtering happens.
       */
      this.captureOriginalSlides();

      this.initSlider();
      

      document.body.addEventListener(
        "touchstart",
        () => {
          this.querySelectorAll(
            "deferred-media[data-autoplay]"
          ).forEach((media) => {
            const iframe = media.querySelector("iframe");

            if (!iframe) return;

            if (iframe.classList.contains("js-youtube")) {
              iframe.contentWindow.postMessage(
                '{"event":"command","func":"' +
                  "playVideo" +
                  '","args":""}',
                "*"
              );
            }

            if (iframe.classList.contains("js-vimeo")) {
              iframe.contentWindow.postMessage(
                '{"method":"play"}',
                "*"
              );
            }
          });
        },
        { once: true }
      );
    }

    /*
     * ---------------------------------------------------------
     * STORE ORIGINAL SLIDES
     * ---------------------------------------------------------
     */

    captureOriginalSlides() {
      /*
       * Don't capture again.
       */
      if (
        this.originalMainSlides.length ||
        this.originalThumbSlides.length
      ) {
        return;
      }

      const mainList =
        this.mainSlider.element?.querySelector(".splide__list");

      if (mainList) {
        this.originalMainSlides = Array.from(
          mainList.children
        ).filter(
          (slide) =>
            !slide.classList.contains(
              "splide__slide--clone"
            )
        );
      }

      const thumbList =
        this.thumbsSlider.element?.querySelector(
          ".splide__list"
        );

      if (thumbList) {
        this.originalThumbSlides = Array.from(
          thumbList.children
        ).filter(
          (slide) =>
            !slide.classList.contains(
              "splide__slide--clone"
            )
        );
      }
    }

    /*
     * ---------------------------------------------------------
     * CONTROLS
     * ---------------------------------------------------------
     */

    onControlClick(
      event,
      sliderInstance = this.mainSlider.instance
    ) {
      event.preventDefault();

      if (!sliderInstance) {
        return;
      }

      const action = event.target.hasAttribute("data-prev")
        ? "<"
        : ">";

      sliderInstance.go(action);
    }

    /*
     * ---------------------------------------------------------
     * INITIALIZE SLIDER
     * ---------------------------------------------------------
     */

    initSlider() {
      if (
        window.innerWidth >= 990 &&
        !this.mainSlider.element.hasAttribute(
          "data-main-slider-desktop"
        )
      ) {
        this.mainSlider.element.classList.add(
          "is-initialized"
        );

        if (this.mainSlider.instance) {
          this.mainSlider.instance.destroy(true);
          this.mainSlider.instance = null;
        }

        if (this.thumbsSlider.instance) {
          this.thumbsSlider.instance.destroy(true);
          this.thumbsSlider.instance = null;
        }

        return;
      }

      if (this.mainSlider.instance) {
        return;
      }

      /*
       * Make sure original slides have been captured.
       */
      this.captureOriginalSlides();

      if (this.thumbsSlider.element) {
        this.thumbsSlider.instance = new Splide(
          this.thumbsSlider.element,
          this.thumbsSlider.options
        ).mount();
      }

      this.mainSlider.instance = new Splide(
        this.mainSlider.element,
        this.mainSlider.options
      );

      this.bindMainSliderEvents();

      this.thumbsSlider.instance
        ? this.mainSlider.instance
            .sync(this.thumbsSlider.instance)
            .mount()
        : this.mainSlider.instance.mount();
    }

    /*
     * ---------------------------------------------------------
     * MAIN SLIDER EVENTS
     * ---------------------------------------------------------
     */

    bindMainSliderEvents() {
      if (this.autoHeight) {
        this.mainSlider.instance.on("mounted", () => {
          this.changeListHeight(
            this.mainSlider.instance.Components.Elements.root,
            this.mainSlider.instance.Components.Elements.slides
          );
        });

        this.mainSlider.instance.on("moved", () => {
          this.changeListHeight(
            this.mainSlider.instance.Components.Elements.root,
            this.mainSlider.instance.Components.Elements.slides
          );
        });
      }

      if (this.mainSlider.bar) {
        this.mainSlider.instance.on("mounted move", () => {
          const sliderEnd =
            this.mainSlider.instance.Components.Controller.getEnd() +
            1;

          this.mainSlider.bar.style.width = `${
            (100 *
              (this.mainSlider.instance.index + 1)) /
            sliderEnd
          }%`;
        });
      }
    }

    /*
     * ---------------------------------------------------------
     * AUTO HEIGHT
     * ---------------------------------------------------------
     */

    changeListHeight(rootEl, slides) {
      const activeSlide = slides.find((slide) =>
        slide.classList.contains("is-active")
      );

      if (!activeSlide) return;

      const list = rootEl.querySelector(".splide__list");

      if (list) {
        list.style.height = `${activeSlide.offsetHeight}px`;
      }
    }

    /*
     * ---------------------------------------------------------
     * VARIANT MEDIA JSON
     * ---------------------------------------------------------
     */

    getVariantMediaData() {
      const sectionId =
        this.dataset.section ||
        this.closest("[data-section-id]")?.dataset.sectionId ||
        "";

      const dataElement = document.getElementById(
        `variant-media-data-${sectionId}`
      );

      if (!dataElement) {
        /*
         * Fallback for product pages where there is only
         * one variant-media JSON element.
         */
        const fallbackElement = document.querySelector(
          'script[id^="variant-media-data-"]'
        );

        if (!fallbackElement) {
          return [];
        }

        try {
          const data = JSON.parse(
            fallbackElement.textContent
          );

          return Array.isArray(data.variantMedia)
            ? data.variantMedia
            : [];
        } catch (error) {
          console.warn(
            "Unable to parse variant media data:",
            error
          );

          return [];
        }
      }

      try {
        const data = JSON.parse(
          dataElement.textContent
        );

        return Array.isArray(data.variantMedia)
          ? data.variantMedia
          : [];
      } catch (error) {
        console.warn(
          "Unable to parse variant media data:",
          error
        );

        return [];
      }
    }

    /*
     * ---------------------------------------------------------
     * UPDATE VARIANT MEDIA
     * ---------------------------------------------------------
     *
     * Example:
     *
     * Original:
     * Variant A
     * Variant B
     * Variant C
     * Common 1
     * Common 2
     * Common 3
     *
     * Select Variant B:
     *
     * Variant B
     * Common 1
     * Common 2
     * Common 3
     *
     * Select Variant C:
     *
     * Variant C
     * Common 1
     * Common 2
     * Common 3
     *
     * Variant A and B are NOT lost because we always
     * rebuild from originalMainSlides.
     */

   
    updateVariantMedia(variantId, selectedMediaId) {
      if (!this.mainSlider.element) {
        return;
      }

      /*
      * Make absolutely sure the original slides exist.
      */
      this.captureOriginalSlides();

      const variantMediaData = this.getVariantMediaData();

      /*
      * If there is no variant media JSON, just go to the
      * selected media if one exists.
      */
      if (!variantMediaData.length) {
        if (selectedMediaId) {
          this.goToSlide(selectedMediaId);
        }
        return;
      }

      const variantMediaIds = new Set(
        variantMediaData
          .map((item) => Number(item.mediaId))
          .filter(Boolean)
      );

      const selectedId = selectedMediaId
        ? Number(selectedMediaId)
        : null;

      /*
      * -------------------------------------------------------
      * MAIN SLIDES
      * -------------------------------------------------------
      */

      let selectedMainSlide = null;

      if (selectedId) {
        selectedMainSlide = this.originalMainSlides.find(
          (slide) =>
            Number(slide.dataset.mediaId) === selectedId
        );
      }

      /*
      * Common slides are slides that do NOT belong to any
      * variant media.
      */
      const commonMainSlides =
        this.originalMainSlides.filter((slide) => {
          const mediaId = Number(slide.dataset.mediaId);

          return !variantMediaIds.has(mediaId);
        });

      /*
      * -------------------------------------------------------
      * ORDER MAIN SLIDES
      * -------------------------------------------------------
      *
      * Variant HAS image:
      *
      *   Variant image
      *   Common image
      *   Common image
      *
      * Variant DOES NOT have image:
      *
      *   Common image
      *   Common image
      *   Common image
      */
      let orderedMainSlides;

      if (selectedMainSlide) {
        orderedMainSlides = [
          selectedMainSlide,
          ...commonMainSlides.filter(
            (slide) => slide !== selectedMainSlide
          ),
        ];
      } else {
        /*
        * No variant image.
        * Show only common images.
        */
        orderedMainSlides = [...commonMainSlides];
      }

      /*
      * -------------------------------------------------------
      * THUMBNAILS
      * -------------------------------------------------------
      */

      let orderedThumbSlides = [];

      if (this.originalThumbSlides.length) {
        let selectedThumbSlide = null;

        if (selectedId) {
          selectedThumbSlide =
            this.originalThumbSlides.find(
              (slide) =>
                Number(slide.dataset.mediaId) === selectedId
            );
        }

        const commonThumbSlides =
          this.originalThumbSlides.filter((slide) => {
            const mediaId = Number(slide.dataset.mediaId);

            return !variantMediaIds.has(mediaId);
          });

        if (selectedThumbSlide) {
          orderedThumbSlides = [
            selectedThumbSlide,
            ...commonThumbSlides.filter(
              (slide) => slide !== selectedThumbSlide
            ),
          ];
        } else {
          /*
          * No variant image:
          * only show common thumbnails.
          */
          orderedThumbSlides = [...commonThumbSlides];
        }
      }

      /*
      * Rebuild both galleries.
      */
      this.rebuildGallery(
        orderedMainSlides,
        orderedThumbSlides
      );
    }



    /*
     * ---------------------------------------------------------
     * REBUILD GALLERY
     * ---------------------------------------------------------
     */

    rebuildGallery(
      orderedMainSlides,
      orderedThumbSlides
    ) {
      /*
       * Destroy MAIN slider first.
       */
      if (this.mainSlider.instance) {
        this.mainSlider.instance.destroy(true);
        this.mainSlider.instance = null;
      }

      /*
       * Destroy THUMB slider too.
       *
       * This is important because the thumbnail slider
       * must have exactly the same media order.
       */
      if (this.thumbsSlider.instance) {
        this.thumbsSlider.instance.destroy(true);
        this.thumbsSlider.instance = null;
      }

      /*
       * Remove any Splide clones.
       */
      this.mainSlider.element
        .querySelectorAll(".splide__slide--clone")
        .forEach((slide) => slide.remove());

      if (this.thumbsSlider.element) {
        this.thumbsSlider.element
          .querySelectorAll(
            ".splide__slide--clone"
          )
          .forEach((slide) => slide.remove());
      }

      /*
       * MAIN GALLERY
       */
      const mainList =
        this.mainSlider.element.querySelector(
          ".splide__list"
        );

      if (mainList) {
        mainList.innerHTML = "";

        orderedMainSlides.forEach((slide) => {
          /*
           * Reset Splide state classes before mounting again.
           */
          slide.classList.remove(
            "is-active",
            "is-visible",
            "is-prev",
            "is-next",
            "is-active"
          );

          mainList.appendChild(slide);
        });
      }

      /*
       * THUMBNAIL GALLERY
       */
      if (this.thumbsSlider.element) {
        const thumbList =
          this.thumbsSlider.element.querySelector(
            ".splide__list"
          );

        if (thumbList) {
          thumbList.innerHTML = "";

          orderedThumbSlides.forEach((slide) => {
            slide.classList.remove(
              "is-active",
              "is-visible",
              "is-prev",
              "is-next"
            );

            thumbList.appendChild(slide);
          });
        }
      }

      /*
       * Desktop configuration where the main slider isn't
       * supposed to be active.
       */
      if (
        window.innerWidth >= 990 &&
        !this.mainSlider.element.hasAttribute(
          "data-main-slider-desktop"
        )
      ) {
        this.mainSlider.element.classList.add(
          "is-initialized"
        );

        return;
      }

      /*
       * Re-create thumbnails first.
       */
      if (this.thumbsSlider.element) {
        this.thumbsSlider.instance = new Splide(
          this.thumbsSlider.element,
          this.thumbsSlider.options
        ).mount();
      }

      /*
       * Re-create main slider.
       */
      this.mainSlider.instance = new Splide(
        this.mainSlider.element,
        this.mainSlider.options
      );

      this.bindMainSliderEvents();

      /*
       * Sync main + thumbnails.
       */
      if (this.thumbsSlider.instance) {
        this.mainSlider.instance
          .sync(this.thumbsSlider.instance)
          .mount();
      } else {
        this.mainSlider.instance.mount();
      }

      /*
       * Always show the selected variant image.
       */
      this.mainSlider.instance.go(0);

      if (this.thumbsSlider.instance) {
        this.thumbsSlider.instance.go(0);
      }
    }

    /*
     * ---------------------------------------------------------
     * GO TO SLIDE
     * ---------------------------------------------------------
     */

    goToSlide(mediaId) {
      if (!this.mainSlider.instance) {
        const media =
          this.mainSlider.element.querySelector(
            `[data-media-id="${mediaId}"]`
          );

        if (!media) {
          return;
        }

        media.scrollIntoView({
          behavior: "smooth",
        });

        return;
      }

      const slideIndex =
        this.mainSlider.instance.s.Elements.slides.findIndex(
          (slide) =>
            Number(slide.dataset.mediaId) ===
            Number(mediaId)
        );

      if (slideIndex < 0) {
        return;
      }

      this.mainSlider.instance.go(slideIndex);
    }
  }

  customElements.define(
    "product-media",
    ProductMedia
  );
})();